import React, { useEffect, useState } from 'react';
import axios from '../../api/axios';
import Layout from '../../common/components/Navbar';
import StatCard from '../../common/components/StatCard'; 
import { 
  Grid, 
  Card, 
  Text, 
  Box, 
  Loader, 
  List, 
  Group, 
  Divider, 
  Badge, 
  Button, 
  rem,
  Collapse,
  Modal,
  Table,
  ScrollArea,
  SimpleGrid,
  Indicator,
  Stack
} from '@mantine/core';
import { Calendar } from '@mantine/dates';
import '@mantine/dates/styles.css';
import { notifications } from '@mantine/notifications'; 
import { 
  IconUsers, IconAlertTriangle, IconCurrencyPeso, IconSpeakerphone, 
  IconChevronDown, IconChevronUp, IconLoader, IconReceipt, IconTag,
  IconMapPin, IconCalendarEvent
} from '@tabler/icons-react'; 
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalResidents: 0,
    residentsList: [],
    delinquentResidents: 0,
    delinquentList: [],
    delinquencyRate: 0,
    monthlyCollection: 0,
    unpaidInvoicesCount: 0,
    unpaidInvoicesList: [],
    currentPrices: {},
    venueList: [], 
    incomeHistory: [],
    announcements: [],
    calendarEvents: [] 
  });
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null); 

  // Modal States
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalContent, setModalContent] = useState(null);

  // Calendar State
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const config = { headers: { Authorization: `Bearer ${user.token}` } };
      const { data } = await axios.get('/api/dashboard', config);
      setStats(data);
    } catch (error) {
      console.error(error);
      notifications.show({
          title: 'Dashboard Error',
          message: 'Failed to load dashboard statistics.',
          color: 'red',
          icon: <IconLoader size={18} />,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExpandClick = (id) => setExpandedId(expandedId === id ? null : id);
  const getPriorityColor = (p) => (p === 'urgent' ? 'red' : p === 'high' ? 'orange' : 'blue');

  // --- MODAL RENDERERS ---
  const openListModal = (type) => {
    let content;
    let title;

    if (type === 'residents') {
        title = 'All Residents';
        content = (
            <Table striped>
                <Table.Thead><Table.Tr><Table.Th>Name</Table.Th><Table.Th>Address</Table.Th><Table.Th>Type</Table.Th></Table.Tr></Table.Thead>
                <Table.Tbody>
                    {stats.residentsList.map(r => (
                        <Table.Tr key={r._id}>
                            <Table.Td>{r.lastName}, {r.firstName}</Table.Td>
                            <Table.Td>Blk {r.address.block} Lot {r.address.lot}</Table.Td>
                            <Table.Td>{r.type.replace('_', ' ')}</Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
        );
    } else if (type === 'delinquent') {
        title = 'Delinquent Accounts';
        content = (
             <Table striped>
                <Table.Thead><Table.Tr><Table.Th>Name</Table.Th><Table.Th>Status</Table.Th></Table.Tr></Table.Thead>
                <Table.Tbody>
                    {stats.delinquentList.map(r => (
                        <Table.Tr key={r._id}>
                            <Table.Td>{r.lastName}, {r.firstName}</Table.Td>
                            <Table.Td><Badge color="red">Delinquent</Badge></Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
        );
    } else if (type === 'unpaid') {
        title = 'Pending Invoices (Unpaid/Overdue)';
        content = (
            <Table striped>
                <Table.Thead><Table.Tr><Table.Th>Resident</Table.Th><Table.Th>Amount</Table.Th><Table.Th>Due Date</Table.Th></Table.Tr></Table.Thead>
                <Table.Tbody>
                    {stats.unpaidInvoicesList.map(inv => (
                        <Table.Tr key={inv._id}>
                            <Table.Td>{inv.resident ? `${inv.resident.lastName}, ${inv.resident.firstName}` : 'Unknown'}</Table.Td>
                            <Table.Td>₱{inv.totalAmount.toLocaleString()}</Table.Td>
                            <Table.Td>{new Date(inv.dueDate).toLocaleDateString()}</Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
        );
    }

    setModalTitle(title);
    setModalContent(content);
    setModalOpen(true);
  };

  // --- CALENDAR HELPERS (FIXED) ---
  
  const isSameDay = (d1, d2) => {
      const date1 = new Date(d1);
      const date2 = new Date(d2);
      
      // Check for validity
      if (isNaN(date1.getTime()) || isNaN(date2.getTime())) return false;
      return date1.toDateString() === date2.toDateString();
  };

  const getDayEvents = (date) => {
      if (!date) return [];
      return stats.calendarEvents.filter(event => {
          return isSameDay(event.start, date);
      });
  };

  const renderDay = (dateProp) => {
      // Ensure we create a valid Date object from the prop
      const date = new Date(dateProp);

      // Safety check: if date is invalid or doesn't have getDate, return empty div
      if (isNaN(date.getTime()) || typeof date.getDate !== 'function') {
          return <div></div>;
      }

      const dayEvents = getDayEvents(date);
      
      return (
          <Indicator size={6} color="red" offset={-2} disabled={dayEvents.length === 0}>
              <div style={{ color: 'var(--mantine-color-text)', zIndex: 1 }}>{date.getDate()}</div>
          </Indicator>
      );
  };

  if (loading) return <Layout><Group justify="center" mt={rem(100)}><Loader size="xl" /></Group></Layout>;

  return (
    <Layout>
      <Text component="h1" fz="xl" fw={700} mb="xl">Dashboard Overview</Text>
      
      <Grid gutter="md" mb="xl">
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Box onClick={() => openListModal('residents')} style={{ cursor: 'pointer' }}>
            <StatCard title="Total Residents" value={stats.totalResidents} icon={IconUsers} color="#1976d2"/>
          </Box>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Box onClick={() => openListModal('delinquent')} style={{ cursor: 'pointer' }}>
            <StatCard title="Delinquent Accounts" value={stats.delinquentResidents} icon={IconAlertTriangle} color="#d32f2f"/>
          </Box>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Box onClick={() => openListModal('unpaid')} style={{ cursor: 'pointer' }}>
            <StatCard title="Unpaid Invoices" value={stats.unpaidInvoicesCount} icon={IconReceipt} color="#ed6c02"/>
          </Box>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard title="Monthly Collection" value={`₱${stats.monthlyCollection.toLocaleString()}`} icon={IconCurrencyPeso} color="#2e7d32"/>
        </Grid.Col>
      </Grid>

      <Grid gutter="md">
        {/* LEFT COLUMN: Charts & Prices */}
        <Grid.Col span={{ base: 12, md: 8 }}>
            <Grid>
                 <Grid.Col span={12}>
                    <Card shadow="sm" padding="lg" radius="md" withBorder>
                        <Text fz="lg" fw={600} mb="sm">Income Overview (Last 6 Months)</Text>
                        <Box h={300}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={stats.incomeHistory}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip formatter={(value) => `₱${value.toLocaleString()}`} />
                                    <Bar dataKey="income" fill="var(--mantine-color-green-6)" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </Card>
                 </Grid.Col>

                 <Grid.Col span={12}>
                    <Card shadow="sm" padding="lg" radius="md" withBorder>
                        <Text fz="lg" fw={600} mb="md">Current Payable Prices</Text>
                        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
                            <Card withBorder p="xs" radius="md">
                                <Group>
                                    <IconTag size={24} color="teal" />
                                    <Box>
                                        <Text size="xs" c="dimmed">Monthly Dues</Text>
                                        <Text fw={700}>₱{stats.currentPrices?.monthly_dues || 0}</Text>
                                    </Box>
                                </Group>
                            </Card>
                            <Card withBorder p="xs" radius="md">
                                <Group>
                                    <IconTag size={24} color="blue" />
                                    <Box>
                                        <Text size="xs" c="dimmed">Sticker Fee</Text>
                                        <Text fw={700}>₱{stats.currentPrices?.sticker_fee || 0}</Text>
                                    </Box>
                                </Group>
                            </Card>
                            {/* DYNAMIC VENUES */}
                            {stats.venueList && stats.venueList.length > 0 ? (
                                stats.venueList.map((venue, index) => (
                                    <Card withBorder p="xs" radius="md" key={index}>
                                        <Group>
                                            <IconMapPin size={24} color="orange" />
                                            <Box>
                                                <Text size="xs" c="dimmed" lineClamp={1}>{venue.name}</Text>
                                                <Text fw={700}>₱{venue.price}</Text>
                                            </Box>
                                        </Group>
                                    </Card>
                                ))
                            ) : (
                                <Card withBorder p="xs" radius="md">
                                    <Group>
                                        <IconMapPin size={24} color="orange" />
                                        <Box>
                                            <Text size="xs" c="dimmed">Venues</Text>
                                            <Text fw={700} c="dimmed" size="xs">None Listed</Text>
                                        </Box>
                                    </Group>
                                </Card>
                            )}
                        </SimpleGrid>
                    </Card>
                 </Grid.Col>
            </Grid>
        </Grid.Col>

        {/* RIGHT COLUMN: Calendar & Announcements */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          {/* CALENDAR WIDGET */}
          <Card shadow="sm" padding="md" radius="md" withBorder mb="md">
              <Group gap="xs" mb="sm">
                  <IconCalendarEvent size={20} color="orange" />
                  <Text fz="lg" fw={600}>Venue Schedule</Text>
              </Group>
              <Group justify="center">
                  <Calendar 
                    static 
                    renderDay={renderDay}
                    getDayProps={(date) => ({
                        selected: isSameDay(date, selectedDate),
                        onClick: () => setSelectedDate(new Date(date)),
                    })}
                  />
              </Group>
              <Divider my="sm" />
              <Text size="sm" fw={600} mb="xs">
                Bookings for {new Date(selectedDate).toLocaleDateString()}:
              </Text>
              <ScrollArea h={100}>
                  {getDayEvents(selectedDate).length > 0 ? (
                      <Stack gap="xs">
                          {getDayEvents(selectedDate).map((evt, i) => (
                              <Card key={i} withBorder p="xs" radius="sm">
                                  <Group justify="space-between">
                                      <Text size="xs" fw={500}>{evt.venue}</Text>
                                      <Badge size="xs" color="orange">{evt.time}</Badge>
                                  </Group>
                                  <Text size="xs" c="dimmed">{evt.title}</Text>
                              </Card>
                          ))}
                      </Stack>
                  ) : (
                      <Text size="xs" c="dimmed" ta="center" py="xs">No bookings.</Text>
                  )}
              </ScrollArea>
          </Card>

          {/* ANNOUNCEMENTS */}
          <Card shadow="sm" padding="lg" radius="md" withBorder h={rem(400)} style={{ overflowY: 'auto' }}>
            <Group gap="xs" mb="sm">
              <IconSpeakerphone style={{ width: rem(20), height: rem(20) }} color="blue" />
              <Text fz="lg" fw={600}>Recent Announcements</Text>
            </Group>
            
            <List style={{ listStyleType: 'none', paddingLeft: 0 }}>
              {stats.announcements.length > 0 ? (
                stats.announcements.map((announcement, index) => {
                  const isExpanded = expandedId === announcement._id;
                  return (
                    <Box key={announcement._id} py="xs">
                      <Group justify="space-between" align="flex-start" style={{ cursor: 'pointer' }} onClick={() => handleExpandClick(announcement._id)}>
                          <Box flex={1}>
                            <Text fz="sm" fw={600}>{announcement.title}</Text>
                          </Box>
                          <Button variant="subtle" size="compact-sm" rightSection={isExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />} />
                      </Group>
                      <Group justify="space-between" align="center" mb="xs">
                          <Badge color={getPriorityColor(announcement.priority)} size="sm" variant="light">
                            {announcement.priority.toUpperCase()}
                          </Badge>
                          <Text fz="xs" c="dimmed">{new Date(announcement.createdAt).toLocaleDateString()}</Text>
                      </Group>
                      <Collapse in={isExpanded} transitionDuration={200}>
                          <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }} mb="xs">{announcement.details}</Text>
                      </Collapse>
                      {!isExpanded && <Text fz="sm" c="dimmed" lineClamp={2} mb="xs">{announcement.details}</Text>}
                      {index < stats.announcements.length - 1 && <Divider my="xs" />}
                    </Box>
                  );
                })
              ) : (
                <Text fz="sm" c="dimmed" ta="center" mt="xl">No recent announcements.</Text>
              )}
            </List>
          </Card>
        </Grid.Col>
      </Grid>

      {/* DETAIL MODAL */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={modalTitle} size="lg" scrollAreaComponent={ScrollArea.Autosize}>
        {modalContent}
      </Modal>
    </Layout>
  );
};

export default Dashboard;